import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { RootState } from "../../../store";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useEvents } from "../../../services/eventService";
import Loading from "../../../components/common/Loading";

const Events = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const {
    data: eventsResponse,
    isLoading,
    error,
    refetch,
  } = useEvents({
    perPage: 9, // Show 9 events per page (3x3 grid)
    page: currentPage,
  });

  const events = eventsResponse?.data?.events || [];
  const pagination = eventsResponse?.data?.pagination;
  const totalEvents = pagination?.total || 0;

  // Trigger animations when data loads
  useEffect(() => {
    if (!isLoading && events.length > 0) {
      setIsDataLoaded(true);
    }
  }, [isLoading, events.length]);

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      <Header />
      <main>
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="py-20 px-4 sm:px-16 bg-gradient-to-br from-emerald-600 via-stone-600 to-emerald-700 text-white relative overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-block mb-6"
            >
              <span className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-sm font-medium border border-white/30">
                🎊 Community Events
              </span>
            </motion.div>
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight">
              {t("events.title")}
            </h1>
            <p className="text-xl md:text-2xl opacity-95 mb-10 font-light max-w-2xl mx-auto leading-relaxed">
              Discover our community events and join the celebration!
            </p>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
              className="inline-block"
            >
              <div className="bg-white/25 backdrop-blur-md rounded-2xl px-8 py-4 border border-white/30 shadow-xl">
                <span className="text-xl font-bold">
                  {totalEvents} Amazing Community Events
                </span>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Events Grid */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16"
        >
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <div className="flex justify-center items-center py-16">
                <Loading />
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md mx-auto">
                  <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                    {t("events.errorLoading")}
                  </h3>
                  <p className="text-red-600 dark:text-red-300 mb-4">
                    {t("events.errorMessage")}
                  </p>
                  <button
                    onClick={() => refetch()}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    {t("events.retry")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {events.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 50 }}
                    animate={
                      isDataLoaded
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: 50 }
                    }
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    {/* Event Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={
                          event.gallery?.[0] ||
                          "https://images.unsplash.com/photo-1511578314322-379afb476865?w=400&h=300&fit=crop"
                        }
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold text-white ${event.status === "completed"
                              ? "bg-green-500"
                              : event.status === "upcoming"
                                ? "bg-emerald-500"
                                : "bg-stone-500"
                            }`}
                        >
                          {t(`events.status.${event.status}`)}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-xl font-bold text-white mb-2">
                          {event.title}
                        </h3>
                        <p className="text-white/90 text-sm line-clamp-2">
                          {event.description}
                        </p>
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <span className="text-lg mr-2">📅</span>
                          <span className="text-sm">{event.date}</span>
                        </div>
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <span className="text-lg mr-2">📍</span>
                          <span className="text-sm">{event.location}</span>
                        </div>
                      </div>

                      {event.attendees && (
                        <div className="mb-4">
                          <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <span className="text-lg mr-2">👥</span>
                            <span className="text-sm">
                              {event.attendees} attendees
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mb-6">
                        <span className="bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 px-3 py-1 rounded-full text-sm font-medium">
                          {event.category}
                        </span>
                        <span className="bg-stone-100 dark:bg-stone-900 text-stone-800 dark:text-stone-200 px-3 py-1 rounded-full text-sm font-medium">
                          {event.duration}
                        </span>
                      </div>

                      <div className="flex flex-col gap-3">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() =>
                            navigate(`/${currentLanguage}/events/${event.id}`)
                          }
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
                        >
                          {t("events.viewDetails")}
                        </motion.button>
                        {event.registrationLink && (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full bg-gradient-to-r from-emerald-500 to-stone-500 hover:from-emerald-600 hover:to-stone-600 text-white py-3 px-4 rounded-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                          >
                            Register Now
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <div className="flex justify-center items-center mt-12 space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  {t("common.previous")}
                </button>

                <div className="flex space-x-1">
                  {Array.from(
                    { length: Math.min(5, pagination.last_page) },
                    (_, i) => {
                      const pageNumber =
                        Math.max(
                          1,
                          Math.min(pagination.last_page - 4, currentPage - 2)
                        ) + i;
                      if (pageNumber > pagination.last_page) return null;

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg ${pageNumber === currentPage
                              ? "bg-emerald-600 text-white"
                              : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                  )}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage(
                      Math.min(pagination.last_page, currentPage + 1)
                    )
                  }
                  disabled={currentPage === pagination.last_page}
                  className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  {t("common.next")}
                </button>
              </div>
            )}
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-20 px-4 sm:px-16 bg-gradient-to-r from-emerald-500 via-stone-500 to-emerald-600 text-white relative overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={
                isDataLoaded
                  ? { scale: 1, opacity: 1 }
                  : { scale: 0.9, opacity: 0 }
              }
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="text-5xl">🎉</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6">
              Stay Connected with Our Community
            </h2>
            <p className="text-xl md:text-2xl mb-10 opacity-95 font-light leading-relaxed max-w-2xl mx-auto">
              Join us for upcoming events and be part of something special!
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-emerald-600 px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:bg-gray-50 transition-all duration-300 border-2 border-transparent hover:border-white/50"
            >
              Get Event Notifications 🔔
            </motion.button>
          </div>
        </motion.section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default Events;
